import * as React from 'react';

import { useQueue } from '@components/Hooks/useQueue';
import { BoxProps } from '@kite-js/core/lib/Box/Box';
import { useAppSetting } from '@contexts/appSetting/hooks/useAppSetting';
import { useAppContext } from '@contexts/app/hooks/useAppContext';
import { useServiceResolver } from '@services/useServiceResolver';
import { SlidingNotification } from './SlidingNotification';
import { NotificationCenterContext, Notification } from './NotificationContext';
import { NotificationBadge } from '@v2/components/Notification/NotificationBadge';
import { useSigninNavigation } from '@contexts/signinNavigation/hooks/useSigninNavigation';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export function withNotification<TProps>(Component: React.FC<TProps>): React.FC<React.PropsWithRef<TProps>> {
    const MemoizedComponent = React.memo<React.FC<TProps>>(Component);

    const ComponentWithNotificationCenter: React.FC<React.PropsWithRef<TProps>> = props => {
        const { queue, dequeue, enqueue, resetQueue } = useQueue<Notification>();
        const { customUI } = useAppSetting();
        const { sdkEffect } = useServiceResolver();
        const { useExternalNotification } = useAppContext();

        const { signInNavigationStore } = useSigninNavigation();
        const useUnifiedAuthPage = useStore(
            signInNavigationStore,
            useShallow(state => state.useUnifiedAuthPage)
        );

        const slideFromTop = customUI?.notification?.slideFromTop || false;
        const autoHide = !useUnifiedAuthPage && customUI?.notification?.autoHide;

        React.useEffect(() => {
            if (queue.length > 1) {
                // replace previous notification
                dequeue();
            }

            if (
                autoHide &&
                queue.length &&
                !queue[0]?.link // never auto hide action notification
            ) {
                const t = setTimeout(dequeue, 4000);
                return () => {
                    clearTimeout(t);
                };
            }
        }, [queue]);

        const renderNotification = React.useCallback(
            (container: string, props: BoxProps = {}) => {
                const notification = !!queue[0] && queue[0].container === container ? queue[0] : undefined;
                return useUnifiedAuthPage ? (
                    <NotificationBadge handleDismiss={dequeue} notification={notification} />
                ) : (
                    <SlidingNotification
                        wrapper={slideFromTop ? {} : props}
                        slideFromTop={slideFromTop}
                        handleDismiss={slideFromTop ? dequeue : undefined}
                        notification={notification}
                    />
                );
            },
            [queue, slideFromTop]
        );

        const pushNotification = React.useCallback(
            (item: Notification) => {
                if (
                    useExternalNotification &&
                    !item.link &&
                    !item.forceInternal // notification with link always use internal
                ) {
                    const message = item.message;
                    sdkEffect.onNotificationPush(message, item.color === 'success');
                    return;
                }

                enqueue(item);
            },
            [queue]
        );

        const resetNotification = React.useCallback(() => {
            if (useExternalNotification) {
                return;
            }
            resetQueue();
        }, [queue]);

        return (
            <NotificationCenterContext.Provider
                // tslint:disable-next-line: jsx-no-multiline-js
                value={{
                    // tslint:disable-next-line: jsx-no-multiline-js
                    renderNotification,
                    pushNotification,
                    resetNotification,
                    notifications: queue,
                }}
            >
                <MemoizedComponent {...props} />
            </NotificationCenterContext.Provider>
        );
    };

    ComponentWithNotificationCenter.displayName = `${Component.displayName || 'Component'}WithNotificationCenter`;

    return ComponentWithNotificationCenter;
}
